interface MentionCandidate {
	user: { name: string | null; email: string }
}

// A prefix hit on the typed part outranks a hit buried inside another name.
const enum Rank {
	FirstNamePrefix = 0,
	OtherNamePrefix = 1,
	EmailPrefix = 2,
	Substring = 3,
}

function nameParts(member: MentionCandidate) {
	return (member.user.name ?? "").trim().split(/\s+/).filter(Boolean)
}

function sortKey(member: MentionCandidate) {
	const parts = nameParts(member)
	return {
		first: parts[0] ?? member.user.email,
		rest: parts.slice(1).join(" "),
		email: member.user.email,
	}
}

function rank(member: MentionCandidate, query: string): Rank | null {
	const parts = nameParts(member).map((part) => part.toLowerCase())
	const email = member.user.email.toLowerCase()

	if (parts[0]?.startsWith(query)) return Rank.FirstNamePrefix
	if (parts.slice(1).some((part) => part.startsWith(query)))
		return Rank.OtherNamePrefix
	if (email.startsWith(query)) return Rank.EmailPrefix
	if (parts.join(" ").includes(query) || email.includes(query))
		return Rank.Substring
	return null
}

export function matchMentions<T extends MentionCandidate>(
	members: readonly T[],
	rawQuery: string,
): T[] {
	const query = rawQuery.trim().toLowerCase()

	const ranked = members.flatMap((member) => {
		const matched = query ? rank(member, query) : Rank.FirstNamePrefix
		return matched === null ? [] : [{ member, rank: matched }]
	})

	return ranked
		.sort((a, b) => {
			if (a.rank !== b.rank) return a.rank - b.rank
			const left = sortKey(a.member)
			const right = sortKey(b.member)
			return (
				left.first.localeCompare(right.first) ||
				left.rest.localeCompare(right.rest) ||
				left.email.localeCompare(right.email)
			)
		})
		.map(({ member }) => member)
}
